/* !
 * @copyright WaterStreet. All rights reserved.
 */

/* eslint-disable max-len */
/* eslint-disable @typescript-eslint/no-explicit-any */

import {
	Component,
	Input,
	OnInit,
	ViewChild
} from '@angular/core';
import {
	UntypedFormControl
} from '@angular/forms';
import {
	Router
} from '@angular/router';
import {
	EntityInstanceApiService
} from '@api/services/entities/entity-instance.api.service';
import {
	DynamicWizardComponent
} from '@dynamicComponents/dynamic-wizard/dynamic-wizard.component';
import {
	PaymentAllocationsComponent
} from '@insurance/components/payment-allocations/payment-allocations.component';
import {
	InsuranceConstants
} from '@insurance/constants/insurance-constants';
import {
	InsuranceService
} from '@insurance/services/insurance.service';
import {
	FormlyFieldConfig
} from '@ngx-formly/core';
import {
	AppConstants
} from '@shared/constants/app.constants';
import {
	FormlyConstants
} from '@shared/constants/formly.constants';
import {
	AnyHelper
} from '@shared/helpers/any.helper';
import {
	ObjectHelper
} from '@shared/helpers/object.helper';
import {
	Activity
} from '@shared/implementations/application-data/activity';
import {
	IDynamicComponentContext
} from '@shared/interfaces/application-objects/dynamic-component-context.interface';
import {
	IDynamicComponent
} from '@shared/interfaces/application-objects/dynamic-component.interface';
import {
	IWizardContext
} from '@shared/interfaces/dynamic-interfaces/wizard-context.interface';
import {
	IEntityInstance
} from '@shared/interfaces/entities/entity-instance.interface';
import {
	ActivityService
} from '@shared/services/activity.service';
import {
	ModuleService
} from '@shared/services/module.service';
import {
	SiteLayoutService
} from '@shared/services/site-layout.service';

/* eslint-enable max-len */

@Component({
	selector: 'cash-check-payment',
	templateUrl: './cash-check-payment.component.html'
})

/**
 * A component representing a wizard step to collect
 * the cash/check payment details.
 *
 * @export
 * @class CashCheckPaymentComponent
 * @implements {OnInit}
 * @implements {IDynamicComponent<DynamicWizardComponent, IWizardContext>}
 */
export class CashCheckPaymentComponent implements OnInit,
		IDynamicComponent<DynamicWizardComponent, IWizardContext> {

	/**
	 * Initializes an instance of the cash/check payment component.
	 *
	 * @param {EntityInstanceApiService} entityInstanceApiService
	 * The entity instance api service used in this component.
	 * @param {SiteLayoutService} siteLayoutService
	 * The site layout service used in this component.
	 * @param {InsuranceService} insuranceService
	 * The insurance service used in this component.
	 * @param {ActivityService} activityService
	 * The activity service used in this component.
	 * @param {Router} router
	 * The router used in this component.
	 * @param {ModuleService} moduleService
	 * The module service used in this component.
	 * @memberof CashCheckPaymentComponent
	 */
	public constructor(
		public entityInstanceApiService: EntityInstanceApiService,
		public siteLayoutService: SiteLayoutService,
		public insuranceService: InsuranceService,
		public activityService: ActivityService,
		public router: Router,
		public moduleService: ModuleService)
	{
	}

	/**
	 * Gets or sets the context of this dynamic component that will be set
	 * during initialization. The source is the content component and
	 * the data will be associated data that we desire to pass explicitly.
	 *
	 * @type {IDynamicComponentContext<
	 * 	DynamicWizardComponent,
	 * 	IWizardContext>}
	 * @memberof CashCheckPaymentComponent
	 */
	@Input() public context: IDynamicComponentContext<
		DynamicWizardComponent,
		IWizardContext>;

	/**
	 * Gets or sets the payment allocations child component reference.
	 *
	 * @type {PaymentAllocationsComponent}
	 * @memberof CashCheckPaymentComponent
	 */
	@ViewChild('paymentAllocations')
	public paymentAllocations: PaymentAllocationsComponent;

	/**
	 * Gets or sets the payment allocation settings retrieved from
	 * the product associated to the policy term.
	 *
	 * @type {any}
	 * @memberof CashCheckPaymentComponent
	 */
	public paymentAllocationSettings: any;

	/**
	 * Gets or sets the policy term entity instance that
	 * is associated with the cash/check payment.
	 *
	 * @type {IEntityInstance}
	 * @memberof CashCheckPaymentComponent
	 */
	public policyTermEntityInstance: IEntityInstance;

	/**
	 * Gets or sets the transaction associated with the payment.
	 *
	 * @type {IEntityInstance}
	 * @memberof CashCheckPaymentComponent
	 */
	public transaction: IEntityInstance;

	/**
	 * Gets or sets the formly layout used in implementing components.
	 *
	 * @type {FormlyFieldConfig[]}
	 * @memberof CashCheckPaymentComponent
	 */
	public dynamicFormlyLayout: FormlyFieldConfig[];

	/**
	 * Gets or sets the context active menu item current data collected on the
	 * dynamic wizard steps.
	 *
	 * @type {any}
	 * @memberof CashCheckPaymentComponent
	 */
	public get currentData(): any
	{
		return this.context.source.activeMenuItem.currentData.data;
	}

	/**
	 * Gets a value indicating whether payment allocations are enabled.
	 *
	 * @type {boolean}
	 * @memberof CashCheckPaymentComponent
	 */
	public get paymentAllocationsEnabled(): boolean
	{
		return this.paymentAllocationSettings?.enabled ?? false;
	}

	/**
	 * Gets the amount field configuration.
	 *
	 * @type {FormlyFieldConfig}
	 * @memberof CashCheckPaymentComponent
	 */
	public get amountFieldConfig(): FormlyFieldConfig
	{
		return this.dynamicFormlyLayout
			?.find(field => field.key === 'amount');
	}

	/**
	 * Gets the transaction field configuration.
	 *
	 * @type {FormlyFieldConfig}
	 * @memberof CashCheckPaymentComponent
	 */
	public get transactionFieldConfig(): FormlyFieldConfig
	{
		return this.dynamicFormlyLayout
			?.find(field => field.key === 'transactionId');
	}

	/**
	 * Gets the transaction amount field configuration.
	 *
	 * @type {FormlyFieldConfig}
	 * @memberof CashCheckPaymentComponent
	 */
	public get transactionAmountFieldConfig(): FormlyFieldConfig
	{
		return this.dynamicFormlyLayout
			?.find(field => field.key === 'transactionAmount');
	}

	/**
	 * Gets the invoice amount field configuration.
	 *
	 * @type {FormlyFieldConfig}
	 * @memberof CashCheckPaymentComponent
	 */
	public get invoiceAmountFieldConfig(): FormlyFieldConfig
	{
		return this.dynamicFormlyLayout
			?.find(field => field.key === 'invoiceAmount');
	}

	/**
	 * Gets the auto allocate button configuration.
	 *
	 * @type {FormlyFieldConfig}
	 * @memberof CashCheckPaymentComponent
	 */
	public get autoAllocateButtonConfig(): FormlyFieldConfig
	{
		return this.dynamicFormlyLayout
			?.find(field => field.key === 'autoAllocateButton');
	}

	/**
	 * Gets the allocation amount field configuration.
	 *
	 * @type {FormlyFieldConfig}
	 * @memberof CashCheckPaymentComponent
	 */
	public get allocationAmountFieldConfig(): FormlyFieldConfig
	{
		return this.dynamicFormlyLayout
			?.find(field => field.key === 'allocationAmount');
	}

	/**
	 * Gets the workflow action name associated to this wizard step.
	 *
	 * @type {string}
	 * @memberof CashCheckPaymentComponent
	 */
	private readonly workflowActionName: string =
		'PaymentCreateLedgerTransaction';

	/**
	 * Implements the on initialization interface.
	 *
	 * @memberof CashCheckPaymentComponent
	 */
	public ngOnInit(): void
	{
		this.initializeComponent();
	}

	/**
	 * Loads data and initializes the controls
	 * required for this component to work.
	 *
	 * @async
	 * @memberof CashCheckPaymentComponent
	 */
	public async initializeComponent(): Promise<void>
	{
		this.policyTermEntityInstance =
			await this.insuranceService.getPolicyTerm(
				this.currentData.id);

		this.paymentAllocationSettings =
			await this.insuranceService
				.getProductPaymentAllocationSettings(
					this.policyTermEntityInstance.data.productName);

		this.initializeLayout();

		this.context.source.addToNext(
			this.makePayment.bind(this));

		this.context.source.wizardStepLoading = false;
	}

	/**
	 * Executes the payment process.
	 *
	 * @async
	 * @memberof CashCheckPaymentComponent
	 */
	public async makePayment(): Promise<void>
	{
		let urlParams: any =
			{
				paymentMethod: this.currentData.paymentType,
				amount: this.currentData.amount,
				receiptDate: this.currentData.receiptDate,
				payor: this.currentData.payor
			};

		if (this.currentData.paymentType
			=== InsuranceConstants.paymentTypes.check)
		{
			urlParams =
				{
					...urlParams,
					checkNumber: this.currentData.checkNumber,
					checkDate: this.currentData.checkDate,
					checkPostmarkDate: this.currentData.postmarkDate
				};
		}

		let requestBody: any = null;

		if (this.paymentAllocationsEnabled)
		{
			urlParams =
				{
					...urlParams,
					transactionId:
						this.currentData.transactionId
				};

			requestBody =
				{
					allocationReferences:
						this.currentData.paymentAllocations
							.map((allocation: any) => ({
								id: allocation.referenceId,
								allocatedAmount: allocation.allocatedAmount
							}))
				};
		}

		this.entityInstanceApiService.entityInstanceTypeGroup =
			this.currentData.entityTypeGroup;

		const queryString: string =
			this.entityInstanceApiService
				.formUrlParam(
					AppConstants.empty,
					urlParams);

		await this.activityService.handleActivity(
			new Activity<any>(
				this.entityInstanceApiService
					.executeActionWithPayload(
						this.currentData.id,
						this.workflowActionName,
						requestBody,
						queryString),
				'<strong>Processing payment</strong>',
				'<strong>Payment processed</strong>',
				'Payment processed successfully.',
				'Payment failed to process.'));

		await this.navigateToDashboard();
	}

	/**
	 * Navigates the user to the policy term dashboard after payment processing.
	 *
	 * @async
	 * @memberof CashCheckPaymentComponent
	 */
	public async navigateToDashboard(): Promise<void>
	{
		const routeData =
			ObjectHelper.mapRouteData(
				{
					layoutType:
						AppConstants.layoutTypes.full
				});

		this.router.navigate(
			[
				`${this.moduleService.name}/entities`,
				InsuranceConstants.insuranceEntityTypeGroups.policyTerms,
				AppConstants.viewTypes.edit,
				this.currentData.id
			],
			{
				queryParams: {
					routeData: routeData
				}
			});
	}

	/**
	 * Handles the transaction number field change event.
	 *
	 * @async
	 * @param {FormlyFieldConfig} field
	 * The formly field configuration.
	 * @return {Promise<void>}
	 * The promise that resolves when the transaction change event is complete.
	 * @memberof CashCheckPaymentComponent
	 */
	public async onTransactionChange(
		field: FormlyFieldConfig): Promise<void>
	{
		this.transaction =
			AnyHelper.isNullOrWhitespace(
				field.formControl.value)
				? null
				: await this.insuranceService.getPolicyTermTransaction(
					field.formControl.value,
					InsuranceConstants.policyTermTransactionPrefix
						+ `${this.policyTermEntityInstance.data.productName}`);

		this.transactionFieldConfig
			.formControl
			?.updateValueAndValidity();
	}

	/**
	 * Loads the children transactions for the current policy term.
	 *
	 * @return {Promise<IEntityInstance[]>}
	 * The promise containing the list of transactions.
	 * @memberof CashCheckPaymentComponent
	 */
	public loadTransactions(): Promise<IEntityInstance[]>
	{
		return this.insuranceService
			.getPolicyTermTransactionsByPolicyTermId(
				this.currentData.id);
	}

	/**
	 * Handles the validity changed event for the dynamic formly component.
	 * This will update the validity of the wizard step for action buttons.
	 *
	 * @async
	 * @param {boolean} isValid
	 * The validity of the current displayed step data set.
	 * @memberof CashCheckPaymentComponent
	 */
	public async validityChanged(
		isValid: boolean): Promise<void>
	{
		await this.context.source.validStepChanged(
			isValid
				&& (!this.paymentAllocationsEnabled
					|| this.paymentAllocations?.valid));
	}

	/**
	 * Handles the validity changed event for the payment allocations component.
	 * This will update the validity of the wizard step for action buttons.
	 *
	 * @param {boolean} isValid
	 * The validity of the payment allocations component.
	 * @memberof CashCheckPaymentComponent
	 */
	public async paymentAllocationsValidityChanged(
		isValid: boolean): Promise<void>
	{
		await this.context.source.validStepChanged(
			isValid
				&& this.dynamicFormlyLayout[0]
					.formControl
					.parent
					.valid);
	}

	/**
	 * Creates the dynamic formly layout.
	 *
	 * @memberof CashCheckPaymentComponent
	 */
	private initializeLayout(): void
	{
		const checkFieldExpressions = {
			hide: (field: FormlyFieldConfig) =>
				field.model.paymentType
					!== InsuranceConstants.paymentTypes.check,
			'props.required': (field: FormlyFieldConfig) =>
				field.model.paymentType
					=== InsuranceConstants.paymentTypes.check
		};

		const allocationFieldsExpressions: any =
			{
				hide: () =>
					!this.paymentAllocationsEnabled
			};

		this.dynamicFormlyLayout =
			<FormlyFieldConfig[]>
			[
				{
					key: 'paymentType',
					type: FormlyConstants.customControls.customSelect,
					wrappers: [
						FormlyConstants.customControls.customFieldWrapper
					],
					props: {
						label: 'Payment Method',
						required: true,
						options: [
							{
								label: InsuranceConstants.paymentTypes.cash,
								value: InsuranceConstants.paymentTypes.cash
							},
							{
								label: InsuranceConstants.paymentTypes.check,
								value: InsuranceConstants.paymentTypes.check
							},
							{
								label: InsuranceConstants.paymentTypes.wire,
								value: InsuranceConstants.paymentTypes.wire
							}
						],
						default: InsuranceConstants.paymentTypes.check
					}
				},
				{
					key: 'amount',
					type: FormlyConstants.customControls.customInputNumber,
					wrappers: [
						FormlyConstants.customControls.customFieldWrapper
					],
					props: {
						label: 'Amount',
						required: true,
						useCurrency: true
					},
					validators: {
						amountMustBeGreaterThanZero: {
							expression: (control: UntypedFormControl) =>
								!AnyHelper.isNull(control.value)
									&& control.value > 0,
							message: 'Amount must be greater than zero'
						}
					}
				},
				{
					key: 'receiptDate',
					type: FormlyConstants.customControls.customCalendar,
					wrappers: [
						FormlyConstants.customControls.customFieldWrapper
					],
					props: {
						label: 'Receipt Date',
						required: true
					}
				},
				{
					key: 'payor',
					type: FormlyConstants.customControls.input,
					wrappers: [
						FormlyConstants.customControls.customFieldWrapper
					],
					props: {
						label: 'Payor',
						required: true
					}
				},
				{
					key: 'transactionId',
					type: FormlyConstants.customControls.customDataSelect,
					wrappers: [
						FormlyConstants.customControls.customFieldWrapper
					],
					props: {
						label: 'Transaction',
						placeholder: 'Select an Option',
						required: true,
						showClear: true,
						dataPromise:
							this.loadTransactions.bind(this),
						labelTemplate: '${item.data.transactionNumber}',
						valueTemplate: '${item.id}',
						change:
							this.onTransactionChange.bind(this)
					},
					expressions:
						allocationFieldsExpressions,
					validators: {
						transactionAmountMustBeGreaterThanZero: {
							expression: () =>
								AnyHelper.isNull(
									this.transaction)
									|| AnyHelper.isNull(
										this.transaction
											.data
											.accounting
											?.directWrittenTotal)
									|| this.transaction
										.data
										.accounting
										.directWrittenTotal > 0,
							message:
								'Payment cannot be applied to a transaction '
									+ 'with a negative Transaction Amount.'
						}
					}
				},
				{
					key: 'transactionAmount',
					type: FormlyConstants.customControls.customInputNumber,
					wrappers: [
						FormlyConstants.customControls.customFieldWrapper
					],
					props: {
						label: 'Transaction Amount',
						useCurrency: true,
						disabled: true
					},
					expressions:
						allocationFieldsExpressions
				},
				{
					key: 'invoiceAmount',
					type: FormlyConstants.customControls.customInputNumber,
					wrappers: [
						FormlyConstants.customControls.customFieldWrapper
					],
					props: {
						label: 'Invoice Amount',
						useCurrency: true,
						disabled: true
					},
					expressions:
						allocationFieldsExpressions
				},
				{
					key: 'allocationAmount',
					type: FormlyConstants.customControls.customInputNumber,
					wrappers: [
						FormlyConstants.customControls.customFieldWrapper
					],
					props: {
						label: 'Allocation Amount',
						useCurrency: true,
						disabled: true
					},
					expressions:
						allocationFieldsExpressions
				},
				{
					key: 'autoAllocateButton',
					type: FormlyConstants.customControls.customOperationButton,
					wrappers: [],
					props: {
						label: 'Auto Allocate',
						hideLabel: true,
						gridColumns: 6
					},
					expressions:
						allocationFieldsExpressions
				},
				{
					wrappers: [
						FormlyConstants.customControls.customEmptyWrapper
					],
					props: {
						gridColumns: 12
					}
				},
				{
					key: 'checkNumber',
					type: FormlyConstants.customControls.input,
					wrappers: [
						FormlyConstants.customControls.customFieldWrapper
					],
					props: {
						label: 'Check Number'
					},
					expressions: checkFieldExpressions
				},
				{
					key: 'checkDate',
					type: FormlyConstants.customControls.customCalendar,
					wrappers: [
						FormlyConstants.customControls.customFieldWrapper
					],
					props: {
						label: 'Check Date'
					},
					expressions: checkFieldExpressions
				},
				{
					key: 'postmarkDate',
					type: FormlyConstants.customControls.customCalendar,
					wrappers: [
						FormlyConstants.customControls.customFieldWrapper
					],
					props: {
						label: 'Postmark Date'
					},
					expressions: checkFieldExpressions
				}
			];
	}
}